import { useState } from 'react';
import { useAuth } from '../auth-lib';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Input } from '../design-system';
import AuthLayout, { AuthPasswordInput, AuthTypeToggle } from '../components/AuthLayout';
import { ADMIN_BRAND } from '../components/authBrand';
import { TotpScanStep, TotpRecoveryCodesStep } from '../components/TotpSetupSteps';
import { sanitizeLoginCode, isCompleteCode, RECOVERY_INPUT_LENGTH } from '../lib/otpInput';

// Sign-in is now up to four screens, because a password is no longer
// necessarily a session and an email address is no longer necessarily one
// account:
//   password → done                                    (no second factor)
//   password → firm → …                                (this address opens several firms)
//   password → code                                    (authenticator enrolled)
//   password → scan → recovery codes → done            (role requires one, none set up)
// The server decides which by what it answers; this component never guesses.

function Login() {
  const [step, setStep] = useState('password'); // password | firm | code | scan | recovery
  const [mode, setMode] = useState('firm'); // 'firm' | 'employee' — tailors copy
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [mfaToken, setMfaToken] = useState('');
  const [setup, setSetup] = useState(null);
  // The firms this address opened, when there was more than one. ⚠️ `password`
  // is deliberately still in state at this point: answering the choice re-posts
  // it, because the server proves the password again rather than issuing a
  // challenge token for the second step.
  const [choices, setChoices] = useState([]);
  const [recoveryCodes, setRecoveryCodes] = useState([]);
  // Confirming a forced enrolment signs the user in and returns them, so the
  // recovery-code screen already knows where to send them next.
  const [signedInUser, setSignedInUser] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, completeMfaLogin, startTotpSetup, confirmTotpSetup } = useAuth();
  const navigate = useNavigate();

  // Route by role LEVEL, not name, so a firm's custom roles land correctly too.
  const goHome = (user) => {
    if (user?.isSuperAdmin) navigate('/admin');
    else if ((user?.role?.level ?? 0) >= 4) navigate('/org-admin');
    else navigate('/employee');
  };

  // One submit handler for both the password screen and the firm choice, since
  // the choice is answered on the SAME endpoint with the same password — see
  // the note on `choices`. `accountId` is undefined on the first pass.
  const attemptLogin = async (accountId) => {
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password, { accountId });

      if (result?.organizationChoiceRequired) {
        setChoices(result.organizations || []);
        setStep('firm');
        return;
      }

      if (result?.mfaRequired) {
        setMfaToken(result.mfaToken);
        setStep('code');
        return;
      }

      if (result?.mfaSetupRequired) {
        // Their role requires an authenticator and they have none. The enrol
        // challenge stands in for the access token they don't hold yet.
        setMfaToken(result.mfaToken);
        setSetup(await startTotpSetup({ mfaToken: result.mfaToken }));
        setStep('scan');
        return;
      }

      goHome(result?.user);
    } catch (err) {
      setError(err.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handlePassword = (e) => {
    e.preventDefault();
    attemptLogin();
  };

  const handleCode = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await completeMfaLogin({ mfaToken, code });
      goHome(result?.user);
    } catch (err) {
      setError(err.error || 'That code was not accepted.');
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  const handleConfirmSetup = async (typedCode) => {
    setError('');
    setLoading(true);
    try {
      const result = await confirmTotpSetup({ code: typedCode, mfaToken });
      // Confirming on this path signs them in as well, so the only thing left
      // is to make them save their recovery codes before they leave.
      setRecoveryCodes(result.recoveryCodes || []);
      setSignedInUser(result.user || null);
      setStep('recovery');
    } catch (err) {
      setError(err.error || 'That code was not accepted.');
    } finally {
      setLoading(false);
    }
  };

  const restart = () => {
    setStep('password');
    setMfaToken('');
    setSetup(null);
    setChoices([]);
    setCode('');
    setError('');
  };

  // A CA is routinely a partner at one practice and an article at another, so
  // the same address opens more than one workspace. Which one is asked here
  // rather than guessed — the two accounts have different roles and different
  // clients, and signing somebody into the wrong practice is not a small error.
  if (step === 'firm') {
    return (
      <AuthLayout
        {...ADMIN_BRAND}
        title="Which firm?"
        subtitle={`${email} has an account at more than one firm`}
        error={error}
        footer={
          <button type="button" className="totp-link" onClick={restart}>
            Back to sign in
          </button>
        }
      >
        <div className="auth-firm-list">
          {choices.map((choice) => (
            <button
              key={choice.accountId}
              type="button"
              className="auth-firm-option"
              onClick={() => attemptLogin(choice.accountId)}
              // A deactivated account at one of the firms is shown and refused
              // here rather than after another password round trip, so the
              // reason is attached to the firm it belongs to.
              disabled={loading || !choice.isActive}
            >
              <span className="auth-firm-name">{choice.name}</span>
              <span className="auth-firm-meta">
                {choice.isActive ? choice.roleName || 'Staff' : 'Account disabled'}
              </span>
            </button>
          ))}
        </div>
      </AuthLayout>
    );
  }

  if (step === 'code') {
    return (
      <AuthLayout
        {...ADMIN_BRAND}
        title="Two-factor authentication"
        subtitle="Enter the 6-digit code from your authenticator app"
        error={error}
        footer={
          <button type="button" className="totp-link" onClick={restart}>
            Back to sign in
          </button>
        }
      >
        <form onSubmit={handleCode} className="auth-form">
          <Input
            label="Code"
            id="totp-code"
            inputMode="text"
            placeholder="123456"
            value={code}
            // Recovery codes go in the same box — they are 12 characters and a
            // TOTP code is 6 digits, so the server can tell them apart and the
            // user never has to say which they typed. That is also why this
            // caps at 14 rather than 6: a recovery code may open with six
            // digits, and a tighter cap would make it impossible to type.
            onChange={(e) => setCode(sanitizeLoginCode(e.target.value))}
            maxLength={RECOVERY_INPUT_LENGTH}
            required
            disabled={loading}
            autoComplete="one-time-code"
            autoFocus
            helperText="Lost your phone? A recovery code works here too."
          />
          <Button
            type="submit"
            variant="primary"
            size="lg"
            loading={loading}
            className="auth-submit"
            disabled={!isCompleteCode(code)}
          >
            {loading ? 'Verifying…' : 'Verify'}
          </Button>
        </form>
      </AuthLayout>
    );
  }

  if (step === 'scan') {
    return (
      <AuthLayout
        {...ADMIN_BRAND}
        title="Set up two-factor authentication"
        subtitle="Your role requires an authenticator app before you can continue"
      >
        <TotpScanStep
          setup={setup}
          onConfirm={handleConfirmSetup}
          loading={loading}
          error={error}
          onCancel={restart}
        />
      </AuthLayout>
    );
  }

  if (step === 'recovery') {
    return (
      <AuthLayout
        {...ADMIN_BRAND}
        title="Save your recovery codes"
        subtitle="Two-factor authentication is on"
      >
        <TotpRecoveryCodesStep
          codes={recoveryCodes}
          doneLabel="Continue to your dashboard"
          onDone={() => goHome(signedInUser)}
        />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      {...ADMIN_BRAND}
      title="Welcome back"
      subtitle={mode === 'firm' ? "Sign in to your firm's console" : 'Sign in to your employee workspace'}
      error={error}
      footer={
        mode === 'firm' ? (
          <>
            <Link to="/forgot-password">Forgot password?</Link>
            <div className="auth-divider">New here?</div>
            <Link to="/register">Register your firm — free</Link>
          </>
        ) : (
          <>
            <Link to="/forgot-password">Forgot password?</Link>
            <div className="auth-divider">Need access?</div>
            <span>Your firm's admin adds you from their Users page.</span>
          </>
        )
      }
    >
      <AuthTypeToggle value={mode} onChange={setMode} />
      <form onSubmit={handlePassword} className="auth-form">
        <Input
          label="Email"
          id="email"
          type="email"
          placeholder="you@firm.com"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          required
          disabled={loading}
          autoComplete="email"
        />

        <AuthPasswordInput
          label="Password"
          id="password"
          placeholder="••••••••••"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          disabled={loading}
          autoComplete="current-password"
        />

        <Button type="submit" variant="primary" size="lg" loading={loading} className="auth-submit">
          {loading ? 'Signing in…' : 'Sign In'}
        </Button>
      </form>
    </AuthLayout>
  );
}

export default Login;
